import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";
import { getProject } from "../lib/api";

export default function ProjectPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProject(null);
    setError(false);
    getProject(slug).then(setProject).catch(() => setError(true));
  }, [slug]);

  const accent = project?.accent || "#6B8F71";

  if (project && project.full_case) {
    return (
      <div className="relative bg-[#1A1A1A]">
        <header className="fixed top-0 inset-x-0 z-50 bg-[#1A1A1A]/85 backdrop-blur-md border-b border-white/10">
          <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-14 sm:h-16 flex items-center justify-between">
            <Link
              to="/"
              data-testid="project-brand"
              className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-white flex items-center gap-2.5"
            >
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#6B8F71]" />
              Sanya Vithalkar
            </Link>
            <div className="flex items-center gap-5 sm:gap-7">
              <a
                href="/#work"
                data-testid="project-back-work"
                className="font-sans text-sm tracking-wide text-white/90 hover:text-[#C9A24B] transition-colors inline-flex items-center gap-2"
              >
                <ArrowLeft size={16} />
                Back to work
              </a>
            </div>
          </nav>
        </header>
        <iframe
          src={project.tool_url}
          title={project.title}
          data-testid="project-iframe"
          allow="fullscreen"
          allowFullScreen
          className="w-full border-0 block bg-white mt-14 sm:mt-16 h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)]"
        />
      </div>
    );
  }

  return (
    <div className="grain relative bg-[#FFFFFF] min-h-screen">
      {/* top bar */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#1A1A1A]/85 backdrop-blur-md border-b border-white/10">
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="project-brand"
            className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-white flex items-center gap-2.5"
          >
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#6B8F71]" />
            Sanya Vithalkar
          </Link>
          <a
            href="/#work"
            data-testid="project-back-work"
            className="font-sans text-sm tracking-wide text-white/90 hover:text-[#C9A24B] transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to work
          </a>
        </nav>
      </header>

      <main className="pt-28 sm:pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          {error && (
            <div data-testid="project-not-found" className="py-24 text-center">
              <h1 className="font-serif text-3xl text-ink">Project not found</h1>
              <Link to="/" className="inline-flex items-center gap-2 mt-6 font-sans text-sm text-[#6B8F71]">
                <ArrowLeft size={16} /> Back home
              </Link>
            </div>
          )}

          {project && (
            <>
              <Reveal>
                <div className="flex items-center gap-2.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: accent }} />
                  <span className="font-mono text-xs tracking-[0.28em] uppercase" style={{ color: accent }}>
                    {project.category}
                  </span>
                </div>
                <h1
                  data-testid="project-title"
                  className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink mt-4 leading-[1.05]"
                >
                  {project.title}
                </h1>
                {project.subtitle && (
                  <p className="font-serif italic text-lg sm:text-xl text-ink/70 mt-3 max-w-2xl">
                    {project.subtitle}
                  </p>
                )}
                {project.description && (
                  <p className="font-sans text-base sm:text-lg text-ink/80 mt-5 max-w-2xl leading-relaxed">
                    {project.description}
                  </p>
                )}
              </Reveal>

              {/* live interactive tool */}
              {project.tool_url && (
                <Reveal delay={0.05}>
                  <div
                    className="mt-10 rounded-2xl overflow-hidden border shadow-[0_30px_60px_-40px_rgba(26,26,26,0.45)]"
                    style={{ borderColor: `${accent}55` }}
                  >
                    <iframe
                      src={project.tool_url}
                      title={project.title}
                      data-testid="project-iframe"
                      allow="fullscreen"
                      allowFullScreen
                      className="w-full h-[600px] sm:h-[760px] bg-white border-0 block"
                    />
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                    <a
                      href={project.tool_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="project-open-tool"
                      className="inline-flex items-center gap-2 font-sans text-sm px-6 py-3 rounded-full text-white transition-transform duration-300 hover:-translate-y-0.5"
                      style={{ backgroundColor: accent }}
                    >
                      {project.cta_label || "Open the tool"}
                      <ArrowUpRight size={16} />
                    </a>
                    <span className="font-mono text-xs text-ink/50">
                      Live &amp; interactive. Open in a new tab for full screen
                    </span>
                  </div>
                </Reveal>
              )}

              {/* problem / research / solution */}
              <div className="mt-16 grid md:grid-cols-3 gap-8 lg:gap-10">
                {[
                  ["Problem", project.problem],
                  ["Research", project.research],
                  ["Solution", project.solution],
                ].map(([label, body], bi) =>
                  body ? (
                    <Reveal key={label} delay={bi * 0.08}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs" style={{ color: accent }}>
                            0{bi + 1}
                          </span>
                          <h2 className="font-sans text-xs tracking-[0.22em] uppercase text-ink">
                            {label}
                          </h2>
                        </div>
                        <p className="font-sans text-sm text-ink/75 leading-relaxed mt-3 whitespace-pre-line">
                          {body}
                        </p>
                      </div>
                    </Reveal>
                  ) : null
                )}
              </div>

              <Reveal delay={0.1}>
                <a
                  href="/#work"
                  data-testid="project-footer-back"
                  className="inline-flex items-center gap-2 mt-20 font-sans text-sm text-[#6B8F71] hover:text-ink transition-colors"
                >
                  <ArrowLeft size={16} /> Back to all work
                </a>
              </Reveal>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
